import { useCallback, useState } from "react";
import { buildAuthHeaders } from "../../api/client.ts";
import { downloadExportByPath } from "../../api/fileUpload.ts";
import { Button } from "../../components/ui/button.tsx";
import { StatusIcon } from "../../components/ui/statusIcon.tsx";
import { useOptionalTools } from "../../tools/ToolsDrawer.tsx";
import { formatIssaFileSize, issaFileKindLabel } from "./issaAttachments.ts";
import type { IssaGeneratedFile } from "./issaInsightsApi.ts";
import styles from "./issaInsights.module.scss";

/** My files - "Exported Reports" in tools/toolRegistry.ts. */
const MY_FILES_TOOL = "exportedreports";

/** Where the copy of an answer's file is downloaded: the ordinary export
 *  download, which serves it to its owner only. */
export function issaFileDownloadPath(exportId: string): string {
  return `/api/v2/userfile/download?exportId=${encodeURIComponent(exportId)}`;
}

/**
 * A file the answer offers, at its place in the answer: what it is, and a
 * Download button. The file is a copy in the user's My files, so it outlives the
 * conversation; once My files has pruned it the card says so instead of
 * offering a download that would fail.
 */
export function IssaAnswerFile({ exportId, file }: {
  /** The id in the answer's tag. */
  exportId: string;
  /** What the server knows about it; missing when the conversation read could not say. */
  file?: IssaGeneratedFile;
}) {
  const tools = useOptionalTools();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gone, setGone] = useState(file?.Expired === true);
  const name = file?.FileName || "File";
  const kind = issaFileKindLabel(name, file?.MediaType);
  const size = formatIssaFileSize(file?.SizeBytes ?? 0);

  const download = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await downloadExportByPath(issaFileDownloadPath(exportId), name, await buildAuthHeaders());
    } catch (err) {
      if (err instanceof Error && err.message.startsWith("Download 404")) setGone(true);
      else setError("The file could not be downloaded. Try again.");
    } finally {
      setBusy(false);
    }
  }, [exportId, name]);

  return (
    <div className={`${styles.fileCard}${gone ? ` ${styles.fileCardGone}` : ""}`}>
      <span className={styles.fileCardIcon} aria-hidden="true"><StatusIcon kind="file" size={24} /></span>
      <div className={styles.fileCardText}>
        <span className={styles.fileCardName} title={name}>{name}</span>
        <span className={styles.fileCardMeta}>
          {gone ? "This file is no longer available" : [kind, size].filter(Boolean).join(" · ")}
        </span>
      </div>
      {!gone && (
        <div className={styles.fileCardActions}>
          <Button variant="primary" size="sm" loading={busy} onClick={() => void download()} aria-label={`Download ${name}`}>
            <span aria-hidden="true"><StatusIcon kind="download" size={14} /></span> Download
          </Button>
          {tools && (
            <Button variant="ghost" size="sm" onClick={() => tools.openTools(MY_FILES_TOOL)}>
              Show in Exported Reports
            </Button>
          )}
        </div>
      )}
      {error && <span className={styles.fileCardError} role="alert">{error}</span>}
    </div>
  );
}
